/**
 * How the viewer decides whether the prototype actually came up (spec R3.7).
 *
 * ============================================================================
 * WHY THIS IS A GUESS, AND HOW THE GUESS IS MADE SAFE
 * ============================================================================
 * A cross-origin sandboxed iframe gives the parent almost nothing to work with.
 * There is no `error` event for an HTTP failure, `contentDocument` is `null`
 * across the opaque origin, and the `load` event fires for a 404 body just as
 * happily as for a real prototype. So "did it load?" cannot be answered
 * directly — only bounded.
 *
 * Three signals are combined:
 *
 *  - the `load` event, which at least proves *something* was rendered;
 *  - `atlas:ready` from the injected agent, which proves the document is a
 *    prototype the viewer can actually work with;
 *  - two timeouts, because the absence of either signal is only meaningful
 *    after enough time has passed to rule out a slow network.
 *
 * The two failures are kept apart because they are not the same problem and do
 * not have the same remedy. `unreachable` means nothing rendered at all — the
 * user is staring at an empty rectangle and needs an explanation and a retry.
 * `unresponsive` means the prototype is on screen and perfectly usable, but the
 * agent never answered, so commenting is unavailable; that is a notice beside a
 * working prototype, never an error screen over the top of one.
 */

export type FrameStatus =
  /** Navigation started; nothing has rendered yet. */
  | 'loading'
  /** The `load` event fired. Something is on screen. */
  | 'loaded'
  /** The agent announced itself. The channel is live. */
  | 'ready'
  /** No `load` event within the budget. Almost certainly a blank frame. */
  | 'unreachable'
  /** Rendered, but no agent. Browsing works; commenting does not. */
  | 'unresponsive';

export type FrameEvent =
  /** A new document is being loaded: retry, version switch, screen change. */
  | 'reset'
  | 'load'
  | 'agent-ready'
  | 'load-timeout'
  | 'agent-timeout';

/**
 * Generous, because this budget is competing with a reviewer's patience on a
 * conference wifi, and crying "could not load" over a prototype that was about
 * to appear is worse than fifteen seconds of a spinner.
 */
export const FRAME_LOAD_TIMEOUT_MS = 15_000;

/**
 * Shorter, because it starts only once `load` has already fired: the document
 * is parsed and the agent script is a few kilobytes that were served from the
 * same origin as the document. If it has not spoken in eight seconds it is not
 * going to.
 */
export const FRAME_AGENT_TIMEOUT_MS = 8_000;

export const INITIAL_FRAME_STATUS: FrameStatus = 'loading';

/**
 * The whole state machine, as a pure function.
 *
 * ## Why `load` never demotes `ready`
 *
 * The obvious reading — "`load` means a new document, so the agent must speak
 * again" — is wrong, and wrong in the direction that breaks the viewer. The
 * agent script is injected into the document's own markup and runs during
 * parsing, so `atlas:ready` reaches the parent *before* the iframe's `load`
 * event, which waits for every image and stylesheet. Treating `load` as a
 * demotion would therefore knock the viewer out of `ready` a fraction of a
 * second after it correctly got there, on every single load, and the comment
 * toggle would flicker off just as the prototype appeared.
 *
 * The reviewer clicking a link inside the prototype is the same sequence: the
 * new document's agent announces itself, then `load` fires. Only a
 * parent-initiated change — a retry, a version switch — sends `reset`, and that
 * one is emitted before the new document exists.
 */
export const nextFrameStatus = (
  status: FrameStatus,
  event: FrameEvent,
): FrameStatus => {
  switch (event) {
    case 'reset':
      return 'loading';

    case 'load':
      return status === 'ready' ? 'ready' : 'loaded';

    case 'agent-ready':
      return 'ready';

    case 'load-timeout':
      // Only meaningful while still waiting for the first paint. Once `load`
      // has fired the budget is spent and the timer's opinion is stale.
      return status === 'loading' ? 'unreachable' : status;

    case 'agent-timeout':
      return status === 'loaded' ? 'unresponsive' : status;

    default:
      return status;
  }
};

/** Whether the frame is showing nothing and needs an error state over it. */
export const isFrameBlank = (status: FrameStatus): boolean =>
  status === 'unreachable';

/** Whether the agent channel is usable — pins, navigation, comment mode. */
export const isAgentUsable = (status: FrameStatus): boolean =>
  status === 'ready';

/**
 * A sentence for the states that owe the user one, and `null` for the states
 * that speak for themselves.
 *
 * `loading` returns `null` deliberately: a spinner already says "loading", and
 * a caption repeating it is noise on top of the one screen that is meant to
 * stay quiet.
 */
export const frameStatusNotice = (status: FrameStatus): string | null => {
  switch (status) {
    case 'unreachable':
      return 'The prototype did not load. It may still be uploading, the file server may be unreachable, or this version may be missing its files.';

    case 'unresponsive':
      return 'The prototype is loaded, but Atlas cannot reach the review agent inside it. Browsing works; comments and pins are unavailable until it responds.';

    default:
      return null;
  }
};
